import EntityType from "@mapmycustomers/shared/enum/EntityType";
import {
  ChatCreatableEntityType,
  ChatCreateRecordAction,
  ChatProposalAction,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import { Group } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { RawFile } from "@mapmycustomers/shared/types/File";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import {
  isRecord,
  RECORD_PARENT_ENTITY_TYPES,
} from "@mapmycustomers/shared/util/chat/chatWireGuards";

import { ADDRESS_COLUMNS } from "./chatAddress";
import { formToWireValues, wireToFormValues } from "./createRecordValues";

export const CREATE_RECORD_ENTITY_TYPE_KEY = "finalEntityType";
export const CREATE_RECORD_LAYOUT_ID_KEY = "finalLayoutId";
export const CREATE_RECORD_FILES_KEY = "draftedFiles";
export const CREATE_RECORD_ADDED_FIELDS_KEY = "addedFieldNames";

// The whole uploaded file, not just its id: the wire carries ids alone, and nothing can look the
// rest up afterwards — the uploaded file lives in the Create modal's own store slice, which the next
// modal open clears. The card draws a name from this, and a reopened editor seeds its uploader with
// it, which is why the whole object is kept rather than a name.
export type DraftedFile = RawFile;

// A persisted draft outlives the build that wrote it, so entries are checked rather than cast. The
// two fields checked are the ones everything downstream needs: the id a create is given, and the
// name a row draws.
const isDraftedFile = (value: unknown): value is DraftedFile =>
  isRecord(value) && typeof value.id === "number" && typeof value.name === "string";

const filterDraftedFiles = (value: unknown): DraftedFile[] =>
  Array.isArray(value) ? value.filter(isDraftedFile) : [];

// Five keys rather than a flat value bag: four are not record values — two are instructions to the
// apply call and two are editor state — and a flat bag could not tell a field named "finalLayoutId"
// from the layout id itself.
interface CreateRecordDraft {
  // Which fields the rep opened through "+ Add Field", named the way the field model names them.
  // Named rather than inferred from `values`: a field the rep added and left empty has no value to
  // infer from, and it still has to be there when they come back to fill it in.
  [CREATE_RECORD_ADDED_FIELDS_KEY]?: IField["name"][];
  [CREATE_RECORD_ENTITY_TYPE_KEY]?: ChatCreatableEntityType;
  // Beside `values`, deliberately never inside it: every key under `values` is sent to the platform,
  // which takes `files` as a list of ids, so the names have nowhere else to ride.
  [CREATE_RECORD_FILES_KEY]?: DraftedFile[];
  [CREATE_RECORD_LAYOUT_ID_KEY]?: number;
  values: Record<string, unknown>;
}

// A draft written before this key existed reads as no files rather than as a crash.
export const readDraftedFiles = (draft?: Record<string, unknown>): DraftedFile[] =>
  filterDraftedFiles(draft?.[CREATE_RECORD_FILES_KEY]);

// Same reasoning as readDraftedFiles: a persisted draft outlives the build that wrote it, so the
// entries are filtered rather than cast, and a draft from before this key reads as none.
export const readDraftedAddedFields = (draft?: Record<string, unknown>): IField["name"][] => {
  const names = draft?.[CREATE_RECORD_ADDED_FIELDS_KEY];
  return Array.isArray(names)
    ? names.filter((name): name is IField["name"] => typeof name === "string")
    : [];
};

// The form's file control holds whole RawFile objects, so this is the one moment they can be
// captured; formToWireValues reduces the same list to the ids the platform is given.
const draftedFilesOf = (values: Record<string, unknown>): DraftedFile[] =>
  filterDraftedFiles(values.files);

// The draft store is typed Record<string, unknown> and a persisted draft outlives the build that
// wrote it, so every read is guarded rather than cast.
const readDraftValues = (
  draft: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!draft || !isRecord(draft.values)) {
    return undefined;
  }
  return draft.values;
};

// customFields is a set addressed by id, not a sequence: the id-keyed form record reorders it to
// ascending id on save, so this one key compares as a set rather than by position.
const isSameCustomFields = (left: unknown, right: unknown): boolean => {
  if (!Array.isArray(left) || !Array.isArray(right)) {
    return JSON.stringify(left) === JSON.stringify(right);
  }
  const sorted = (list: unknown[]) => list.map((item) => JSON.stringify(item)).sort();
  return JSON.stringify(sorted(left)) === JSON.stringify(sorted(right));
};

// Compared key by key rather than as one JSON string: a draft round-tripped through the form has no
// guaranteed key order relative to the wire's own proposedValues.
const isValuesEqual = (left: Record<string, unknown>, right: Record<string, unknown>): boolean => {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return Array.from(keys).every((key) =>
    key === "customFields"
      ? isSameCustomFields(left[key], right[key])
      : JSON.stringify(left[key]) === JSON.stringify(right[key])
  );
};

// Only these three are legal here; a stored draft outlives the build that wrote it, so the value is
// re-checked rather than cast.
export const readFinalEntityType = (
  draft?: Record<string, unknown>
): ChatCreatableEntityType | undefined =>
  RECORD_PARENT_ENTITY_TYPES.find(
    (candidate) => candidate === draft?.[CREATE_RECORD_ENTITY_TYPE_KEY]
  );

// The layout the editor last built this draft against, and by the same token whether the editor
// wrote it at all: the modal records the layout on every Save and Save is disabled until one
// resolves, while the card's own type picker writes a draft that never had a layout to record.
export const readSavedLayoutId = (
  draft?: Record<string, unknown>
): FormLayout["id"] | undefined => {
  const layoutId = draft?.[CREATE_RECORD_LAYOUT_ID_KEY];
  return typeof layoutId === "number" ? layoutId : undefined;
};

// The action always wins: the platform ignores finalEntityType on a typed proposal, so honouring a
// draft there would show the rep one type and create another.
export const resolveCreateRecordEntityType = (
  action: ChatCreateRecordAction,
  draft?: Record<string, unknown>
): ChatCreatableEntityType | undefined => action.targetEntityType ?? readFinalEntityType(draft);

// Seeded from the rep's saved draft when there is one, else the proposal; wireToFormValues does the
// wire <-> form shape conversion.
export const resolveCreateRecordInitialValues = (
  action: ChatCreateRecordAction,
  draft: Record<string, unknown> | undefined,
  customFieldDefinitions: CustomField[]
): Record<string, unknown> => {
  const values = wireToFormValues(
    readDraftValues(draft) ?? action.proposedValues,
    customFieldDefinitions
  );
  // The value bag holds file ids alone. The form wants the objects the draft kept beside them: the
  // uploader renders them, and Save reads their names back out of the form.
  const files = readDraftedFiles(draft);
  return files.length ? { ...values, files } : values;
};

// What Save builds from the live form: formToWireValues does the shape conversion, then the
// per-entity rules a Person (no settable name) and a Deal (no address, phone or email) enforce.
export const draftFromFormValues = (
  entityType: ChatCreatableEntityType,
  // Must be the PARENT layout's id: finalLayoutId resolves against the rep's assigned parent
  // layouts, so a funnel/stage child layout's id is absent, and the create would be refused.
  layoutId: number | undefined,
  values: Record<string, unknown>,
  customFieldDefinitions: CustomField[],
  // The org's groups for this entity type: the form holds group ids alone, so this is what puts the
  // name back on the wire shape the card and the platform both read.
  groups: Group[],
  // The fields the rep opened through "+ Add Field". Optional so the many call sites that predate
  // it keep reading as "the rep added nothing", which is what they mean.
  addedFieldNames: IField["name"][] = []
): Record<string, unknown> => {
  const wireValues = formToWireValues(values, customFieldDefinitions, groups);

  if (entityType === EntityType.PERSON) {
    delete wireValues.name;
  }
  if (entityType === EntityType.DEAL) {
    ADDRESS_COLUMNS.forEach((key) => delete wireValues[key]);
    delete wireValues.geoManagementState;
    delete wireValues.geoPoint;
    delete wireValues.phone;
    delete wireValues.email;
  }

  // The draft store is keyed by string and this interface carries no index signature, so the typed
  // draft is spread into a fresh object before it is handed back.
  const draft: CreateRecordDraft = {
    [CREATE_RECORD_ENTITY_TYPE_KEY]: entityType,
    values: wireValues,
  };
  if (layoutId !== undefined) {
    draft[CREATE_RECORD_LAYOUT_ID_KEY] = layoutId;
  }
  const draftedFiles = draftedFilesOf(values);
  if (draftedFiles.length) {
    draft[CREATE_RECORD_FILES_KEY] = draftedFiles;
  }
  if (addedFieldNames.length) {
    draft[CREATE_RECORD_ADDED_FIELDS_KEY] = addedFieldNames;
  }
  return { ...draft };
};

// The finalValues for an accepted create_record, or undefined when the rep never opened the modal.
// Unlike record_update, not a diff against the proposal: the platform writes the whole set given.
export const toCreateRecordFinalValues = (
  action: ChatCreateRecordAction,
  draft: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  const values = readDraftValues(draft);
  if (!values) {
    return undefined;
  }
  const finalValues: Record<string, unknown> = { ...values };
  // [] would win the platform's `??` and wipe every proposed custom field; omitting the key is how
  // "the rep did not touch custom fields" is expressed instead.
  if (Array.isArray(finalValues.customFields) && finalValues.customFields.length === 0) {
    delete finalValues.customFields;
  }
  return finalValues;
};

// Compares the chosen entity type, the layout id, and the values — not values alone, which would
// discard a typeless proposal's type-only draft and leave the platform with no type to create.
export const isCreateRecordDraftEqual = (
  action: ChatProposalAction,
  draft: Record<string, unknown>
): boolean => {
  if (action.type !== "create_record") {
    return false;
  }
  if (resolveCreateRecordEntityType(action, draft) !== action.targetEntityType) {
    return false;
  }
  // The proposal never specifies a layout, so any chosen layout is itself a change.
  if (typeof draft[CREATE_RECORD_LAYOUT_ID_KEY] === "number") {
    return false;
  }
  const values = readDraftValues(draft);
  return !!values && isValuesEqual(values, action.proposedValues);
};
